import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Printer, ReceiptText, X } from 'lucide-react';
import { formatIqd, fromMillidinars, type BillDetailDto } from '@gc/shared';

import { PoweredOffNote } from '@/components/PoweredOffNote';
import { api, ApiError } from '@/lib/api';
import { serviceChargeSign } from '../tabs/checkoutPreview';
import { useBillDetail } from './useBills';

interface BillDetailsDrawerProps {
  billId: string;
  currencySymbol: string;
  onClose: () => void;
}

/**
 * Bill details (SPEC §5.6 "Details (i)") and reprint (§10 "reprint from Bills").
 *
 * The drawer doubles as the printable receipt so that what comes out of the
 * printer is exactly what the employee just checked on screen.
 */
export function BillDetailsDrawer({ billId, currencySymbol, onClose }: BillDetailsDrawerProps) {
  const { t, i18n } = useTranslation();
  const { detail, loading, error } = useBillDetail(billId);
  const [printError, setPrintError] = useState<string | null>(null);
  const [printerNotice, setPrinterNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(
    null,
  );
  const [sendingToPrinter, setSendingToPrinter] = useState(false);

  const money = (millidinars: string) =>
    formatIqd(fromMillidinars(millidinars), { currencySymbol, locale: i18n.language });

  const dateTime = (iso: string | null) =>
    iso
      ? new Intl.DateTimeFormat(i18n.language, {
          dateStyle: 'medium',
          timeStyle: 'short',
          numberingSystem: 'latn',
        }).format(new Date(iso))
      : '—';

  async function handlePrint(): Promise<void> {
    setPrintError(null);
    try {
      // Counted server-side before the dialog opens, so a reprint is on record
      // even if the employee cancels at the browser's print dialog — an
      // uncounted reprint is exactly what a duplicate-receipt dispute needs.
      await api.post(`/bills/${billId}/print`);
    } catch (caught) {
      setPrintError(caught instanceof ApiError ? caught.message : 'Could not record the reprint.');
    }
    window.print();
  }

  /**
   * Wave 3 #18 — reprint through the venue's receipt printer instead of the
   * browser. The server rebuilds the receipt, queues it on the Default Printer
   * and hands it to the print bridge; failures land on Settings → Print Jobs.
   */
  async function handlePrintToPrinter(): Promise<void> {
    setPrinterNotice(null);
    setSendingToPrinter(true);
    try {
      await api.post(`/bills/${billId}/print-to-printer`);
      setPrinterNotice({ kind: 'ok', text: t('bills.printToPrinterQueued') });
    } catch (caught) {
      setPrinterNotice({
        kind: 'error',
        text: caught instanceof ApiError ? caught.message : t('bills.printToPrinterFailed'),
      });
    } finally {
      setSendingToPrinter(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/60"
      role="dialog"
      aria-modal="true"
    >
      {/*
       * Print scoping. The receipt lives deep inside the React tree, so hiding
       * `body > *` would hide it too; toggling visibility instead reaches every
       * ancestor while leaving the receipt subtree painted.
       */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .gc-bill-print, .gc-bill-print * { visibility: visible !important; }
          .gc-bill-print {
            position: absolute !important;
            inset-inline-start: 0 !important;
            top: 0 !important;
            width: 100% !important;
            max-width: none !important;
            border: 0 !important;
            box-shadow: none !important;
            background: #fff !important;
            color: #000 !important;
            padding: 0 !important;
          }
          .gc-no-print { display: none !important; }
        }
      `}</style>

      <button
        type="button"
        aria-label={t('common.cancel')}
        onClick={onClose}
        className="gc-no-print flex-1 cursor-default"
      />

      <aside className="flex h-full w-full max-w-md flex-col overflow-auto border-s border-subtle bg-raised">
        <header className="gc-no-print flex items-center justify-between border-b border-subtle p-4">
          <h2 className="text-lg font-semibold text-content">{t('bills.detailsTitle')}</h2>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => void handlePrint()}
              disabled={!detail}
              title={t('common.print')}
              aria-label={t('common.print')}
              className="grid h-8 w-8 place-items-center rounded-control text-content-secondary hover:bg-hover hover:text-content disabled:opacity-40"
            >
              <Printer className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => void handlePrintToPrinter()}
              disabled={!detail || sendingToPrinter}
              title={t('bills.printToPrinter')}
              aria-label={t('bills.printToPrinter')}
              className="grid h-8 w-8 place-items-center rounded-control text-content-secondary hover:bg-hover hover:text-content disabled:opacity-40"
            >
              <ReceiptText className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.cancel')}
              className="grid h-8 w-8 place-items-center rounded-control text-content-secondary hover:bg-hover hover:text-content"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </header>

        {printError ? (
          <p role="alert" className="gc-no-print px-4 pt-3 text-sm text-danger">
            {printError}
          </p>
        ) : null}

        {printerNotice ? (
          <p
            role={printerNotice.kind === 'error' ? 'alert' : 'status'}
            className={`gc-no-print px-4 pt-3 text-sm ${
              printerNotice.kind === 'error' ? 'text-danger' : 'text-success'
            }`}
          >
            {printerNotice.text}
          </p>
        ) : null}

        {loading ? (
          <p className="p-4 text-sm text-content-muted">{t('common.loading')}</p>
        ) : error ? (
          <p role="alert" className="p-4 text-sm text-danger">
            {error}
          </p>
        ) : detail ? (
          <div className="gc-bill-print p-4">
            <ReceiptHeader detail={detail} />

            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <Field label={t('bills.columns.invoiceNumber')} value={detail.invoiceNumber} />
              {/* The number on the customer's paper (owner, 2026-09-23) — the
                  one they quote when they come back about it. */}
              {detail.receiptNumber ? (
                <Field
                  label={t('serviceCharge.bills.receiptNumber')}
                  value={`#${detail.receiptNumber}`}
                />
              ) : null}
              <Field label={t('bills.businessDate')} value={detail.businessDate} />
              <Field label={t('bills.columns.customer')} value={detail.customerName ?? '—'} />
              <Field label={t('bills.columns.visitor')} value={detail.visitorName ?? '—'} />
              {detail.deviceName ? (
                <Field label={t('bills.columns.deviceNumber')} value={detail.deviceName} />
              ) : null}
              {detail.tabName ? <Field label={t('bills.tab')} value={detail.tabName} /> : null}
              <Field label={t('bills.issuedBy')} value={detail.issuedByName ?? '—'} />
              <Field
                label={t('bills.status')}
                value={t(`bills.statuses.${detail.status}`, detail.status)}
              />

              {detail.reservation ? (
                <>
                  <Field
                    label={t('bills.columns.startDate')}
                    value={dateTime(detail.reservation.startedAt)}
                  />
                  <Field
                    label={t('bills.columns.endDate')}
                    value={dateTime(detail.reservation.endedAt)}
                    note={<PoweredOffNote endedReason={detail.reservation.endedReason} />}
                  />
                  <Field
                    label={t('bills.columns.reservationDuration')}
                    value={detail.reservation.duration}
                  />
                  <Field
                    label={t('bills.columns.hourlyRate')}
                    value={money(detail.reservation.hourlyRateMd)}
                  />
                  <Field
                    label={t('bills.columns.numberOfRounds')}
                    value={String(detail.reservation.numberOfRounds)}
                  />
                  <Field
                    label={t('bills.columns.roundPrice')}
                    value={money(detail.reservation.roundPriceMd)}
                  />
                </>
              ) : null}

              {detail.refill ? (
                <>
                  <Field
                    label={t('bills.columns.refillAmount')}
                    value={money(detail.refill.amountMd)}
                  />
                  <Field
                    label={t('bills.columns.rechargeBonus')}
                    value={`${money(detail.refill.bonusMd)} (${detail.refill.bonusPercentApplied}%)`}
                  />
                  <Field
                    label={t('bills.columns.balanceAfter')}
                    value={money(detail.refill.balanceAfterMd)}
                  />
                </>
              ) : null}
            </dl>

            {detail.lines.length > 0 ? (
              <table className="mt-4 w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-subtle">
                    <th scope="col" className="py-1 text-start text-xs text-content-muted">
                      {t('common.name')}
                    </th>
                    <th scope="col" className="py-1 text-end text-xs text-content-muted">
                      {t('bills.columns.itemCount')}
                    </th>
                    <th scope="col" className="py-1 text-end text-xs text-content-muted">
                      {t('common.total')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {detail.lines.map((line, index) => (
                    <tr key={`${line.name}-${index}`} className="border-b border-subtle/50">
                      <td className="py-1 text-content">
                        {line.name}
                        {line.note ? (
                          <span className="block text-xs text-content-muted">{line.note}</span>
                        ) : null}
                      </td>
                      <td className="tabular py-1 text-end text-content">{line.quantity}</td>
                      <td className="tabular py-1 text-end text-content">
                        {money(line.lineTotalMd)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}

            <dl className="mt-4 space-y-1 border-t border-subtle pt-3 text-sm">
              <Total label={t('bills.columns.price')} value={money(detail.subtotalMd)} />
              {/* Added before the discount, so it reads between them; absent
                  entirely on a bill that carried none. */}
              {detail.serviceChargeMd && detail.serviceChargeMd !== '0' ? (
                <Total
                  label={t('serviceCharge.line', { percent: detail.serviceChargePercent })}
                  value={`${serviceChargeSign(detail.serviceChargeMd)}${money(detail.serviceChargeMd)}`}
                />
              ) : null}
              <Total label={t('bills.columns.discount')} value={money(detail.discountMd)} />
              <Total label={t('common.total')} value={money(detail.totalMd)} strong />
            </dl>

            {detail.payments.length > 0 ? (
              <section className="mt-4 border-t border-subtle pt-3">
                <h3 className="mb-1 text-xs uppercase tracking-wide text-content-muted">
                  {t('bills.payments')}
                </h3>
                {detail.payments.map((payment) => (
                  <p key={payment.id} className="flex justify-between text-sm text-content">
                    <span data-payment-method={payment.method}>
                      {t(`bills.paymentMethods.${payment.method}`)}
                      {/* "Electronic payment — FIB": which app the money went
                          through, which the method alone never says. */}
                      {payment.method === 'ELECTRONIC' && payment.electronicProviderLabel
                        ? ` — ${payment.electronicProviderLabel}`
                        : ''}
                      {payment.employeeName ? ` · ${payment.employeeName}` : ''}
                    </span>
                    <span className="tabular">{money(payment.amountMd)}</span>
                  </p>
                ))}
              </section>
            ) : null}

            {detail.receiptEditorName ? (
              <p className="mt-4 border-t border-subtle pt-3 text-xs text-content-muted">
                {t('bills.editedBy', {
                  name: detail.receiptEditorName,
                  at: dateTime(detail.receiptEditedAt),
                })}
                {detail.discountReason ? ` — ${detail.discountReason}` : ''}
              </p>
            ) : null}

            {detail.voidReason ? (
              <p className="mt-2 text-xs text-danger">
                {t('bills.voidedReason', { reason: detail.voidReason })}
              </p>
            ) : null}

            {detail.printCount > 0 ? (
              <p className="mt-2 text-xs text-content-muted">
                {t('bills.printedTimes', { count: detail.printCount })}
              </p>
            ) : null}
          </div>
        ) : null}
      </aside>
    </div>
  );
}

/** Venue identity, as SPEC §10 requires on every receipt. */
function ReceiptHeader({ detail }: { detail: BillDetailDto }) {
  return (
    <header className="border-b border-subtle pb-3 text-center">
      <p className="text-base font-semibold text-content">{detail.venueName}</p>
      {detail.venueSlogan ? (
        <p className="text-xs text-content-secondary">{detail.venueSlogan}</p>
      ) : null}
      {detail.venueAddress ? (
        <p className="text-xs text-content-muted">{detail.venueAddress}</p>
      ) : null}
      {detail.venuePhone ? <p className="text-xs text-content-muted">{detail.venuePhone}</p> : null}
    </header>
  );
}

/** `note` sits under the value, inside its `<dd>` — the End Date's "PC turned off". */
function Field({ label, value, note }: { label: string; value: string; note?: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-content-muted">{label}</dt>
      <dd className="text-content">
        {value}
        {note}
      </dd>
    </div>
  );
}

function Total({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between">
      <dt className={strong ? 'font-semibold text-content' : 'text-content-secondary'}>{label}</dt>
      {/* The settled figure reads in the current (card bill drawer: "Paid"
          15px cyan) — the one number this drawer exists to show. */}
      <dd
        className={`tabular ${strong ? 'text-[15px] font-semibold text-primary' : 'text-content'}`}
      >
        {value}
      </dd>
    </div>
  );
}
